import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import { getASP, updateASPStatus, withdrawASP } from '@/features/asp/api/aspApi';
import ASPStatusBadge from '@/features/asp/components/ASPStatusBadge';
import ASPActivityTimeline from '@/features/asp/components/ASPActivityTimeline';
import ASPSigningModal from '@/features/asp/components/ASPSigningModal';
import LawyerShareModal from '@/features/asp/components/LawyerShareModal';

export default function ASPReviewPage() {
  const { aspId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [asp, setAsp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [showLawyerModal, setShowLawyerModal] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    loadASP();
  }, [aspId]);

  async function loadASP() {
    try {
      setLoading(true);
      const data = await getASP(aspId);
      setAsp(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleWithdraw() {
    const reason = prompt('Please provide a reason for withdrawing this agreement:');
    if (!reason) return;

    if (!window.confirm('Are you sure you want to withdraw this agreement? This cannot be undone.')) {
      return;
    }

    try {
      setWithdrawing(true);
      await withdrawASP(aspId, reason);
      await loadASP();
    } catch (err) {
      alert('Failed to withdraw agreement: ' + err.message);
    } finally {
      setWithdrawing(false);
    }
  }

  async function handleSendToLawyer() {
    setShowLawyerModal(true);
  }

  async function handleReadyToSign() {
    try {
      await updateASPStatus(aspId, 'ready_to_sign');
      await loadASP();
    } catch (err) {
      alert('Failed to mark as ready to sign: ' + err.message);
    }
  }

  function handleSigningComplete() {
    setShowSigningModal(false);
    loadASP();
  }

  function handleLawyerShareComplete() {
    setShowLawyerModal(false);
    loadASP();
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner spinner-lg text-primary-600"></div>
      </div>
    );
  }

  if (error || !asp) {
    return (
      <div className="container-custom py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800 mb-4">{error || 'Agreement not found'}</p>
          <Link to="/dashboard/buyer" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const isBuyer = profile?.id === asp.buyer_id;
  const isDeveloper = profile?.id === asp.developer_id;
  const canWithdraw = asp.status === 'draft' || asp.status === 'under_review';
  const canSign = asp.status === 'ready_to_sign';
  const isFullySigned = asp.buyer_signed_at && asp.developer_signed_at;

  return (
    <div className="bg-secondary-50 min-h-screen py-8">
      <div className="container-custom">
        <div className="mb-6">
          <Link to="/dashboard/buyer" className="text-primary-600 hover:text-primary-700 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-secondary-900 mb-2">
                    Agreement for Sale & Purchase
                  </h1>
                  <p className="text-secondary-600">Agreement ID: {asp.id.slice(0, 8)}</p>
                </div>
                <ASPStatusBadge status={asp.status} />
              </div>

              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-medium text-blue-900 mb-3">Property Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-blue-700 mb-1">Purchase Price</p>
                      <p className="font-semibold text-blue-900">
                        ${parseFloat(asp.purchase_price_nzd).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-blue-700 mb-1">Deposit Amount</p>
                      <p className="font-semibold text-blue-900">
                        ${parseFloat(asp.deposit_amount_nzd).toLocaleString()}
                      </p>
                    </div>
                    {asp.final_discount_applied && (
                      <div>
                        <p className="text-blue-700 mb-1">Discount Applied</p>
                        <p className="font-semibold text-green-700">
                          ${parseFloat(asp.final_discount_applied).toLocaleString()}
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-blue-700 mb-1">Sunset Date</p>
                      <p className="font-semibold text-blue-900">
                        {new Date(asp.sunset_date).toLocaleDateString('en-NZ')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-secondary-200 pt-6">
                  <h3 className="font-medium text-secondary-900 mb-3">Agreement Terms</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-secondary-600 mb-1">Deposit Due</p>
                      <p className="font-medium">{asp.deposit_due_days} days</p>
                    </div>
                    <div>
                      <p className="text-secondary-600 mb-1">Finance Clause</p>
                      <p className="font-medium">{asp.finance_clause_days} days</p>
                    </div>
                    <div>
                      <p className="text-secondary-600 mb-1">Due Diligence Period</p>
                      <p className="font-medium">{asp.due_diligence_days} days</p>
                    </div>
                    <div>
                      <p className="text-secondary-600 mb-1">Template Version</p>
                      <p className="font-medium">{asp.template_version}</p>
                    </div>
                  </div>
                </div>

                {isFullySigned && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="font-semibold text-green-900">Agreement Fully Executed</p>
                        <p className="text-sm text-green-700">
                          Both parties have signed this agreement
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  {isBuyer && asp.status === 'draft' && (
                    <button
                      onClick={handleSendToLawyer}
                      className="btn-primary"
                    >
                      Share with Lawyer
                    </button>
                  )}

                  {isBuyer && canSign && !asp.buyer_signed_at && (
                    <button
                      onClick={() => setShowSigningModal(true)}
                      className="btn-primary"
                    >
                      Sign Agreement
                    </button>
                  )}

                  {isBuyer && asp.buyer_signed_at && (
                    <div className="flex items-center gap-2 text-green-700">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-medium">You have signed</span>
                    </div>
                  )}

                  {isDeveloper && canSign && !asp.developer_signed_at && (
                    <button
                      onClick={() => setShowSigningModal(true)}
                      className="btn-primary"
                    >
                      Counter-Sign Agreement
                    </button>
                  )}

                  {canWithdraw && (
                    <button
                      onClick={handleWithdraw}
                      disabled={withdrawing}
                      className="btn-outline text-error-600 hover:bg-error-50"
                    >
                      {withdrawing ? 'Withdrawing...' : 'Withdraw Agreement'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="card">
              <h3 className="font-semibold text-secondary-900 mb-4">Activity Timeline</h3>
              <ASPActivityTimeline asp={asp} />
            </div>
          </div>
        </div>

        {showSigningModal && (
          <ASPSigningModal
            asp={asp}
            onClose={() => setShowSigningModal(false)}
            onComplete={handleSigningComplete}
          />
        )}

        {showLawyerModal && (
          <LawyerShareModal
            asp={asp}
            onClose={() => setShowLawyerModal(false)}
            onComplete={handleLawyerShareComplete}
          />
        )}
      </div>
    </div>
  );
}
